import type { FulltimePayroll, ParttimePayroll } from '../PayrollTypes';
import type { MonthlyRecord } from '../../types/models';

export type LaborCategory = 'fulltime' | 'parttime' | 'night';
export type LaborField = 'attendanceDays' | 'holidayWorkDays' | 'holidayWorkMinutes' | 'paidLeaveDays' |
  'absenceDays' | 'lateEarlyMinutes' | 'overtimeMinutes' | 'nightShiftUpTo4' | 'nightShiftOver4' |
  'roundTripDays' | 'mealDeduction' | 'regularWorkMinutes' | 'dayShiftCount' | 'nightShiftCount' |
  'baseMinutes' | 'extraMinutes' | 'deepNightMinutes' | 'deepNightOvertimeMinutes';
export type LaborValues = Partial<Record<LaborField, number | null>>;
export interface LaborRow {
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  category: LaborCategory;
  values: LaborValues;
  autoValues: LaborValues;
  reviewed: boolean;
  adjustmentReason: string;
  excluded?: boolean;
  sourceReviewNote?: string;
}
export interface LaborSnapshot {
  schemaVersion: 1;
  timeEncoding: 'decimal_hours';
  employees: LaborRow[];
}
export interface SubmissionRevision {
  id: string;
  period_key: string;
  period_start: string;
  period_end: string;
  closing_day: number;
  version: number;
  status: 'confirmed' | 'reopened';
  rows: LaborSnapshot;
  source_fingerprint: string;
  confirmed_by: string;
  confirmed_at: string;
  reopened_at: string | null;
  reopen_reason: string | null;
}
export interface PayrollSourceState {
  fingerprint: string;
  pendingAttendanceCount: number;
  pendingOvertimeCount: number;
  missingScheduleCount: number;
  issues?: { employeeId: string; employeeName: string; workDate: string; attendanceId: string;
    kind: 'missing_schedule' | 'overtime_unreported' | 'overtime_unreviewed' | 'attendance_incomplete' }[];
}
export interface LaborFieldDefinition { key: LaborField; label: string; unit: 'days' | 'count' | 'minutes' | 'yen'; column: string; rowOffset?: number; }

export const CATEGORY_LABELS: Record<LaborCategory, string> = { fulltime: '正職員', parttime: '非常勤', night: '夜勤' };
const commonFields: LaborFieldDefinition[] = [
  { key: 'attendanceDays', label: '出勤日数', unit: 'days', column: 'C' },
  { key: 'holidayWorkDays', label: '休出日数', unit: 'days', column: 'D' },
  { key: 'holidayWorkMinutes', label: '休出時間', unit: 'minutes', column: 'D', rowOffset: 1 },
  { key: 'paidLeaveDays', label: '有給日数', unit: 'days', column: 'F' },
];
export const LABOR_FIELDS: Record<LaborCategory, LaborFieldDefinition[]> = {
  fulltime: [...commonFields,
    { key: 'absenceDays', label: '欠勤日数', unit: 'days', column: 'G' },
    { key: 'lateEarlyMinutes', label: '遅刻早退時間', unit: 'minutes', column: 'H' },
    { key: 'overtimeMinutes', label: '普通残業', unit: 'minutes', column: 'J' },
    { key: 'nightShiftUpTo4', label: '夜勤4回まで', unit: 'count', column: 'L' },
    { key: 'nightShiftOver4', label: '夜勤5回目以降', unit: 'count', column: 'M' },
    { key: 'roundTripDays', label: '夜勤者の往復日数', unit: 'days', column: 'N' },
    { key: 'mealDeduction', label: '食事控除', unit: 'yen', column: 'O' },
  ],
  parttime: [...commonFields,
    { key: 'regularWorkMinutes', label: '勤務時間（普通残業を除く）', unit: 'minutes', column: 'G' },
    { key: 'overtimeMinutes', label: '残業時間', unit: 'minutes', column: 'I' },
    { key: 'mealDeduction', label: '食事控除', unit: 'yen', column: 'N' },
  ],
  night: [
    { key: 'attendanceDays', label: '出勤日数', unit: 'days', column: 'D' },
    { key: 'dayShiftCount', label: 'うち日勤回数', unit: 'count', column: 'F' },
    { key: 'nightShiftCount', label: 'うち夜勤回数', unit: 'count', column: 'H' },
    { key: 'paidLeaveDays', label: '有給日数', unit: 'days', column: 'J' },
    { key: 'baseMinutes', label: '基本時間', unit: 'minutes', column: 'P' },
    { key: 'extraMinutes', label: '時間外', unit: 'minutes', column: 'R' },
    { key: 'deepNightMinutes', label: '深夜時間', unit: 'minutes', column: 'T' },
    { key: 'deepNightOvertimeMinutes', label: '深夜残業', unit: 'minutes', column: 'V' },
    { key: 'mealDeduction', label: '食事控除', unit: 'yen', column: 'Y' },
  ],
};

export function formatLaborMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '未確認';
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

export function buildLaborRows(fulltime: FulltimePayroll[], parttime: ParttimePayroll[], monthly: Record<string, MonthlyRecord[]>): LaborRow[] {
  return [...fulltime.map(p => ({ payroll: p, category: 'fulltime' as LaborCategory })),
    ...parttime.map(p => ({ payroll: p, category: 'parttime' as LaborCategory }))].map(({ payroll: p, category }) => {
    const records = monthly[p.employeeId] ?? [];
    const values: LaborValues = {
      attendanceDays: p.attendanceDays, holidayWorkDays: p.holidayWorkDays, holidayWorkMinutes: p.holidayWorkMinutes,
      paidLeaveDays: p.paidLeaveDays, absenceDays: p.absenceDays, lateEarlyMinutes: p.lateEarlyMinutes,
      overtimeMinutes: p.overtimeMinutes, mealDeduction: p.mealDeduction,
      regularWorkMinutes: Math.max(0, p.totalWorkMinutes - p.overtimeMinutes),
      dayShiftCount: records.filter(r => r.shiftType === 'day').length,
      nightShiftCount: records.filter(r => r.shiftType === 'night').length,
      nightShiftUpTo4: category === 'fulltime' ? (p as FulltimePayroll).nightShiftUpTo4 : null,
      nightShiftOver4: category === 'fulltime' ? (p as FulltimePayroll).nightShiftOver4 : null,
      // These definitions have not been agreed with the accountant: never infer zero.
      roundTripDays: null, baseMinutes: null, extraMinutes: null, deepNightMinutes: null, deepNightOvertimeMinutes: null,
    };
    return { employeeId: String(p.employeeId), employeeCode: p.employeeCode, employeeName: p.employeeName,
      category, values, autoValues: { ...values }, reviewed: false, adjustmentReason: '' };
  });
}

export function validateLaborRows(rows: LaborRow[]): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  if (!Array.isArray(rows)) return ['提出対象の従業員データの形式が不正です。'];
  if (rows.length > 2000) issues.push('提出対象は2000行以内にしてください。');
  if (!rows.length) issues.push('提出対象の従業員がいません。');
  rows.forEach(row => {
    if (!row || typeof row.employeeId !== 'string' || !row.employeeId.trim()
        || typeof row.employeeName !== 'string' || !row.employeeName.trim()
        || typeof row.employeeCode !== 'string' || typeof row.adjustmentReason !== 'string'
        || typeof row.reviewed !== 'boolean' || (row.excluded !== undefined && typeof row.excluded !== 'boolean')
        || !row.values || typeof row.values !== 'object' || Array.isArray(row.values)
        || !row.autoValues || typeof row.autoValues !== 'object' || Array.isArray(row.autoValues)
        || !Object.prototype.hasOwnProperty.call(LABOR_FIELDS, row.category)) {
      issues.push('提出対象の従業員行の形式が不正です。'); return;
    }
    const label = `${row.employeeName}（${row.employeeCode || row.employeeId}）`;
    if (!row.employeeId || seen.has(row.employeeId)) issues.push(`${label}: 従業員IDが重複しています。`);
    seen.add(row.employeeId);
    if (!row.reviewed) issues.push(`${label}: 確認が完了していません。`);
    if (row.excluded) {
      if (!row.adjustmentReason.trim()) issues.push(`${label}: 出力対象外にする理由を入力してください。`);
      return;
    }
    if (!LABOR_FIELDS[row.category]) { issues.push(`${label}: シート区分が不正です。`); return; }
    let changed = false;
    for (const field of LABOR_FIELDS[row.category]) {
      const value = row.values[field.key];
      if (value === null || value === undefined || !Number.isFinite(value) || value < 0 || value > 10000000) {
        issues.push(`${label}: ${field.label}を確認・入力してください。`);
      } else if (field.unit !== 'days' && !Number.isInteger(value)) {
        issues.push(`${label}: ${field.label}は整数${field.unit === 'minutes' ? '分' : ''}で入力してください。`);
      }
      if (value !== row.autoValues[field.key]) changed = true;
    }
    if (changed && !row.adjustmentReason.trim()) issues.push(`${label}: 調整値・手入力値の理由を入力してください。`);
  });
  if (rows.length && rows.every(r => r?.excluded)) issues.push('出力対象を少なくとも1名選択してください。');
  return issues;
}

export function payrollPeriod(periodKey: string, closingDay: number): { start: string; end: string } {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(periodKey) || closingDay !== 15) throw new Error('社労士Excelは15日締めの給与期間に対応しています。');
  const [year, month] = periodKey.split('-').map(Number);
  return { start: `${month === 1 ? year - 1 : year}-${String(month === 1 ? 12 : month - 1).padStart(2, '0')}-16`, end: `${periodKey}-15` };
}
